import { useState, useRef } from 'react';
import { STATES, SPECIAL_PLATES, getNeighbors, getStateName } from '../data/states';
import { getPointsLabel } from '../utils/scoring';
import { SearchIcon } from './icons';

interface Props {
  playerState: string | null;
  onScore: (plateState: string) => void;
  disabled?: boolean;
}

export function PlateSelector({ playerState, onScore, disabled }: Props) {
  const [search, setSearch] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  const neighbors = playerState ? getNeighbors(playerState) : [];

  const handleScore = (abbr: string) => {
    if (disabled) return;
    onScore(abbr);
    setSearch('');
    setDropdownOpen(false);
  };

  const filteredStates = STATES.filter(
    s =>
      s.abbr !== playerState &&
      !neighbors.includes(s.abbr) &&
      s.abbr !== 'AK' &&
      s.abbr !== 'HI' &&
      (search === '' ||
        s.name.toLowerCase().includes(search.toLowerCase()) ||
        s.abbr.toLowerCase().includes(search.toLowerCase()))
  );

  const specialOptions = SPECIAL_PLATES.map(p => ({
    abbr: p.abbr,
    name: p.name,
    points: playerState ? getPointsLabel(playerState, p.abbr) : '?',
  }));

  const akHiOptions = STATES.filter(
    s =>
      (s.abbr === 'AK' || s.abbr === 'HI') &&
      s.abbr !== playerState
  );

  return (
    <div className="space-y-4">
      {/* Bordering states */}
      {neighbors.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-atlas-ink-soft dark:text-atlas-ink-soft-dark mb-2">
            Borders {playerState ? getStateName(playerState) : 'You'}
          </p>
          <div className="grid grid-cols-2 gap-2">
            {neighbors.map(abbr => (
              <button
                key={abbr}
                onClick={() => handleScore(abbr)}
                disabled={disabled}
                className="flex items-center justify-between rounded-xl border border-atlas-rule dark:border-atlas-rule-dark bg-atlas-surface dark:bg-atlas-surface-dark px-3.5 py-[15px] hover:bg-atlas-pine/10 dark:hover:bg-atlas-pine-dark/15 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <span className="text-[15px] font-semibold text-atlas-ink dark:text-atlas-ink-dark">{getStateName(abbr)}</span>
                <span className="font-mono text-xs text-atlas-pine dark:text-atlas-pine-dark">+1</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* AK and HI (10 pts) */}
      {akHiOptions.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-atlas-ink-soft dark:text-atlas-ink-soft-dark mb-2">
            {playerState === 'HI' ? 'All States' : 'Alaska & Hawaii'}
          </p>
          <div className="grid grid-cols-2 gap-2">
            {akHiOptions.map(s => (
              <button
                key={s.abbr}
                onClick={() => handleScore(s.abbr)}
                disabled={disabled}
                className="flex items-center justify-between rounded-xl border border-atlas-rule dark:border-atlas-rule-dark bg-atlas-surface dark:bg-atlas-surface-dark px-3.5 py-[15px] hover:bg-atlas-brass/10 dark:hover:bg-atlas-brass-dark/15 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <span className="text-[15px] font-semibold text-atlas-ink dark:text-atlas-ink-dark">{s.name}</span>
                <span className="font-mono text-xs text-atlas-brass dark:text-atlas-brass-dark">+10</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Mexico / Canada */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-atlas-ink-soft dark:text-atlas-ink-soft-dark mb-2">
          International Plates
        </p>
        <div className="grid grid-cols-2 gap-2">
          {specialOptions.map(opt => (
            <button
              key={opt.abbr}
              onClick={() => handleScore(opt.abbr)}
              disabled={disabled}
              className="flex items-center justify-between rounded-xl border border-atlas-rule dark:border-atlas-rule-dark bg-atlas-surface dark:bg-atlas-surface-dark px-3.5 py-[15px] hover:bg-atlas-brick/10 dark:hover:bg-atlas-brick-dark/15 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <span className="text-[15px] font-semibold text-atlas-ink dark:text-atlas-ink-dark">{opt.name}</span>
              <span className="font-mono text-xs text-atlas-brick dark:text-atlas-brick-dark">+20</span>
            </button>
          ))}
        </div>
      </div>

      {/* Searchable dropdown for remaining states */}
      {playerState !== 'HI' && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-atlas-ink-soft dark:text-atlas-ink-soft-dark mb-2">
            All Other States (+6 pts)
          </p>
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-atlas-ink-soft dark:text-atlas-ink-soft-dark pointer-events-none" />
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={e => { setSearch(e.target.value); setDropdownOpen(true); }}
              onFocus={() => setDropdownOpen(true)}
              placeholder="Search states..."
              disabled={disabled}
              className="w-full pl-9 pr-4 py-3 rounded-lg border border-atlas-rule dark:border-atlas-rule-dark bg-atlas-surface dark:bg-atlas-surface-dark text-atlas-ink dark:text-atlas-ink-dark placeholder-atlas-ink-soft/60 dark:placeholder-atlas-ink-soft-dark/60 focus:outline-none focus:ring-2 focus:ring-atlas-brass dark:focus:ring-atlas-brass-dark disabled:opacity-40"
            />
            {dropdownOpen && filteredStates.length > 0 && (
              <div className="absolute z-30 w-full mt-1 max-h-48 overflow-y-auto bg-atlas-surface dark:bg-atlas-surface-dark border border-atlas-rule dark:border-atlas-rule-dark rounded-lg shadow-lg">
                {filteredStates.map(s => (
                  <button
                    key={s.abbr}
                    onMouseDown={() => handleScore(s.abbr)}
                    className="w-full px-4 py-2.5 text-left text-sm hover:bg-atlas-bg dark:hover:bg-atlas-bg-dark flex justify-between items-center text-atlas-ink dark:text-atlas-ink-dark"
                  >
                    <span>
                      <span className="font-bold mr-2">{s.abbr}</span>
                      {s.name}
                    </span>
                    <span className="text-xs text-atlas-brass dark:text-atlas-brass-dark font-medium">
                      {getPointsLabel(playerState, s.abbr)}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {dropdownOpen && search && filteredStates.length === 0 && (
              <div className="absolute z-30 w-full mt-1 bg-atlas-surface dark:bg-atlas-surface-dark border border-atlas-rule dark:border-atlas-rule-dark rounded-lg shadow-lg px-4 py-3 text-sm text-atlas-ink-soft dark:text-atlas-ink-soft-dark">
                No matching states found
              </div>
            )}
          </div>
          {dropdownOpen && (
            <div className="fixed inset-0 z-20" onClick={() => { setDropdownOpen(false); setSearch(''); }} />
          )}
        </div>
      )}

      {/* If in Hawaii, show all states as buttons */}
      {playerState === 'HI' && (
        <div className="grid grid-cols-4 gap-1.5 max-h-56 overflow-y-auto">
          {STATES.filter(s => s.abbr !== 'HI').map(s => (
            <button
              key={s.abbr}
              onClick={() => handleScore(s.abbr)}
              disabled={disabled}
              className="py-2 px-1 rounded-lg bg-atlas-surface dark:bg-atlas-surface-dark text-atlas-ink dark:text-atlas-ink-dark border border-atlas-rule dark:border-atlas-rule-dark font-bold text-xs hover:bg-atlas-rule/30 dark:hover:bg-atlas-rule-dark/40 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {s.abbr}
            </button>
          ))}
        </div>
      )}

      {!playerState && (
        <p className="text-center text-sm text-atlas-ink-soft dark:text-atlas-ink-soft-dark py-4">
          Set your current state to start scoring plates
        </p>
      )}

      {playerState && (
        <p className="text-xs text-atlas-ink-soft dark:text-atlas-ink-soft-dark text-center">
          Tap a plate to score it. Own state (
          <span className="font-semibold text-atlas-ink dark:text-atlas-ink-dark">{playerState}</span>) scores 0 points.
        </p>
      )}
    </div>
  );
}
